import React from "react";
import WebProfitSpinner from "../../components/system/WebProfitSpinner";
import { Stack, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import { activityApi } from "../../api/activity";
import { projectsApi } from "../../api/projects";
import ModuleDisabledState from "../../components/dashboard/ModuleDisabledState";
import DashboardLoadingState from "../../components/dashboard/DashboardLoadingState";
import DesktopWebsiteStats from "../../components/dashboard/stats/DesktopWebsiteStats";
import MobileWebsiteStats from "../../components/dashboard/stats/mobile/MobileWebsiteStats";
import { useProjects } from "../../context/ProjectsContext";
import { isProjectModuleEnabled } from "../../utils/projectModules";
import { toDateInput } from "../../utils/erp";

function startOfMonth() {
  const date = new Date();
  return toDateInput(new Date(date.getFullYear(), date.getMonth(), 1));
}

const MAX_ANALYTICS_RANGE_DAYS = 366;
const DAY_MS = 24 * 60 * 60 * 1000;

function parseDateInput(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const timestamp = Date.UTC(year, month - 1, day);
  const parsed = new Date(timestamp);
  if (parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== day) return null;
  return timestamp;
}

function validateAnalyticsRange(range) {
  if (!range?.dateFrom || !range?.dateTo) {
    return { valid: false, error: "Completá las fechas Desde y Hasta." };
  }
  const from = parseDateInput(range.dateFrom);
  const to = parseDateInput(range.dateTo);
  if (from === null || to === null) {
    return { valid: false, error: "Revisá las fechas elegidas." };
  }
  if (from > to) {
    return { valid: false, error: "La fecha ‘Desde’ no puede ser posterior a ‘Hasta’." };
  }
  const inclusiveDays = Math.floor((to - from) / DAY_MS) + 1;
  if (inclusiveDays > MAX_ANALYTICS_RANGE_DAYS) {
    return { valid: false, error: `Podés consultar hasta ${MAX_ANALYTICS_RANGE_DAYS} días por vez.` };
  }
  return { valid: true, error: "" };
}

function rangeForPreset(preset) {
  const now = new Date();
  const today = toDateInput(now);
  if (preset === "today") return { preset, dateFrom: today, dateTo: today };
  if (preset === "7days") {
    const from = new Date(now);
    from.setDate(now.getDate() - 6);
    return { preset, dateFrom: toDateInput(from), dateTo: today };
  }
  if (preset === "30days") {
    const from = new Date(now);
    from.setDate(now.getDate() - 29);
    return { preset, dateFrom: toDateInput(from), dateTo: today };
  }
  return { preset: "month", dateFrom: startOfMonth(), dateTo: today };
}

export default function ProjectStatsPage() {
  const { projectId } = useParams();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("md"));
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();
  const [project, setProject] = React.useState(null);
  const [analytics, setAnalytics] = React.useState(null);
  const [range, setRange] = React.useState(() => rangeForPreset("30days"));
  const [loadingProject, setLoadingProject] = React.useState(true);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const rangeValidation = React.useMemo(() => validateAnalyticsRange(range), [range]);

  const contextProject = React.useMemo(
    () => projects.find((item) => item.slug === projectId || item.id === projectId || item._id === projectId) || activeProject,
    [projects, projectId, activeProject],
  );
  const resolvedProject = project || contextProject;
  const enabled = isProjectModuleEnabled(resolvedProject, "stats");

  React.useEffect(() => {
    if (!projects.length) loadProjects();
  }, [projects.length, loadProjects]);

  React.useEffect(() => {
    let mounted = true;
    setLoadingProject(true);
    projectsApi.get(projectId)
      .then((data) => {
        if (!mounted) return;
        const nextProject = data.project || null;
        setProject(nextProject);
        if (nextProject) setActiveProject(nextProject);
      })
      .catch((requestError) => {
        if (mounted) setError(requestError.message || "No se pudo cargar el proyecto");
      })
      .finally(() => {
        if (mounted) setLoadingProject(false);
      });
    return () => { mounted = false; };
  }, [projectId, setActiveProject]);

  const loadAnalytics = React.useCallback(async () => {
    if (!enabled || !rangeValidation.valid) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError("");
      const response = await activityApi.businessAnalytics(projectId, {
        dateFrom: range.dateFrom,
        dateTo: range.dateTo,
      });
      setAnalytics(response.analytics || null);
    } catch (requestError) {
      setError(requestError.message || "No se pudieron cargar las estadísticas de actividad del negocio");
    } finally {
      setLoading(false);
    }
  }, [enabled, projectId, range.dateFrom, range.dateTo, rangeValidation.valid]);

  React.useEffect(() => {
    if (!loadingProject) loadAnalytics();
  }, [loadingProject, loadAnalytics]);

  const selectPreset = (preset) => {
    if (preset === "custom") {
      setRange((current) => ({ ...current, preset: "custom" }));
      return;
    }
    setRange(rangeForPreset(preset));
  };

  const updateRange = (key, value) => {
    setRange((current) => ({ ...current, preset: "custom", [key]: value }));
  };

  if (loadingProject) return <DashboardLoadingState label="Cargando estadísticas del negocio…" minHeight={360} />;
  if (!enabled) {
    return (
      <ModuleDisabledState
        projectId={projectId}
        title="Estadísticas"
        description="Este módulo resume la actividad general del negocio a partir del historial: operaciones, equipo, web pública, áreas con movimiento y recorridos."
      />
    );
  }

  const viewProps = {
    analytics,
    range,
    onPreset: selectPreset,
    onRangeChange: updateRange,
    onRefresh: loadAnalytics,
    loading,
    error,
    rangeError: rangeValidation.error,
  };

  return (
    <Stack spacing={1.2}>
      {loading ? <WebProfitSpinner size={24} sx={{ mx: "auto", my: 1 }} /> : null}
      {mobile ? <MobileWebsiteStats {...viewProps} /> : <DesktopWebsiteStats {...viewProps} />}
    </Stack>
  );
}
